/**
 * API helpers for the managed-business invite flow.
 *
 * Service-bureau-created businesses receive an email with a JWT-bearing
 * link that lands on `/:businessSlug/managed-business/invite?token=...`.
 * The page calls these endpoints to validate the token, set the user's
 * password, and authenticate the new owner before redirecting them into
 * the onboarding wizard.
 *
 * The status endpoint is anonymous (token = credential) and surfaces only
 * non-secret context so the landing page can render the bureau / business
 * name beside the password form. The complete endpoint sets the password
 * and issues normal auth cookies; the caller MUST refresh the auth store
 * after a successful response so the rest of the app sees the new user.
 */
import axios, { type AxiosResponse } from "axios";
import type {
  ManagedBusinessInviteCompleteResult,
  ManagedBusinessInviteStatus,
} from "@smartwiz/shared-types";
import { backend_url } from "@/constants";
import { Endpoints } from "@/utils/api/endpoints";
import { AXIOS_CONFIG } from "@/utils/api";

interface BackendResponseShape<T> {
  message: string;
  content: T;
}

/**
 * Validate an invite token and return bureau / business context.
 *
 * Returns ``null`` when the token is invalid or expired so callers can show
 * a clear "request a new invite" message; non-network errors are silenced
 * because the user always lands on this URL from email and a noisy error
 * dialog is more confusing than a polite "this link expired" notification.
 */
export const fetchManagedBusinessInviteStatus = async (
  token: string
): Promise<ManagedBusinessInviteStatus | null> => {
  try {
    const response: AxiosResponse<
      BackendResponseShape<ManagedBusinessInviteStatus>
    > = await axios.get(
      `${backend_url}/${Endpoints.serviceBureau.managedBusinessInviteStatus}`,
      {
        params: { token },
        ...AXIOS_CONFIG,
      }
    );
    if (response.status === 200) {
      return response.data.content;
    }
    return null;
  } catch (error) {
    console.error("Failed to verify managed-business invite token", error);
    return null;
  }
};

/**
 * Consume an invite token, set the new password, and authenticate.
 *
 * Returns the populated status payload + ``authenticated: true`` on
 * success. Throws on unexpected errors so the caller can render a
 * specific message; validation failures (e.g. weak password) come back
 * as a 400 with the human message in ``response.data.message``.
 */
export const completeManagedBusinessInvite = async (
  token: string,
  newPassword: string
): Promise<ManagedBusinessInviteCompleteResult> => {
  const response: AxiosResponse<
    BackendResponseShape<ManagedBusinessInviteCompleteResult>
  > = await axios.post(
    `${backend_url}/${Endpoints.serviceBureau.managedBusinessInviteComplete}`,
    { token, newPassword },
    AXIOS_CONFIG
  );
  return response.data.content;
};
